import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import PropTypes from "prop-types"

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { format } from "date-fns"
import { CalendarIcon, Loader2 } from "lucide-react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import { useState, useEffect, useRef } from "react"
import { useFetchDoctors } from "@/services/query/doctors.query"
import { Card } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { showSuccessToast } from "@/lib/toast"
import { Calendar } from "@/components/shared/date-range-picker/calendar-nw"

const formSchema = z.object({
  doctor_id: z.number({
    required_error: "Please select a doctor",
  }),
  symptoms: z.string().min(1, "Symptoms are required"),
  reason: z.string().optional(),
  addition_notes: z.string().optional(),
  appointment_date: z.date({
    required_error: "Please select a date and time",
  }),
  appointment_time: z.string({
    required_error: "Please select a time",
  }),
  location: z.string({
    required_error: "Please select a location",
  }),
})

const timeSlots = Array.from({ length: 24 * 2 }, (_, i) => {
  const hour = Math.floor(i / 2)
  const minute = i % 2 === 0 ? "00" : "30"
  const period = hour < 12 ? "AM" : "PM"
  let displayHour = hour
  if (hour === 0) {
    displayHour = 12
  } else if (hour > 12) {
    displayHour = hour - 12
  }
  return `${displayHour}:${minute} ${period}`
})

export function AddAppointmentDialog({
  open,
  onOpenChange,
  patientId,
  onSave,
}) {
  const [searchDoctor, setSearchDoctor] = useState("")
  const [openDoctor, setOpenDoctor] = useState(false)
  const [selectedDoctor, setSelectedDoctor] = useState(null)
  const [availableLocations, setAvailableLocations] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      symptoms: "",
      reason: "",
      addition_notes: "",
      location: "",
    },
  })

  const { data: doctorsData, isLoading: isLoadingDoctors } = useFetchDoctors(
    0,
    10,
    searchDoctor
  )

  const searchInputRef = useRef(null)

  useEffect(() => {
    if (!open) {
      setSearchDoctor("")
      setSelectedDoctor(null)
      setAvailableLocations([])
      form.reset()
    }
  }, [open, form])

  useEffect(() => {
    if (searchDoctor) {
      setOpenDoctor(true)
    }
  }, [searchDoctor])

  const handleDoctorSelect = (doctor) => {
    form.setValue("doctor_id", doctor.id)
    setSelectedDoctor(doctor)
    setSearchDoctor(doctor.name)
    setOpenDoctor(false)
    searchInputRef.current?.blur()

    const locations = doctor.location.split(",").map((loc) => loc.trim())
    setAvailableLocations(locations)
    form.setValue("location", locations[0], { shouldValidate: true })
  }

  const onSubmit = async (data) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      const combinedDateTime = new Date(data.appointment_date)
      const [hours, minutes] = data.appointment_time.split(":")
      combinedDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10))

      const appointmentData = {
        patient_id: patientId,
        doctor_id: data.doctor_id,
        symptoms: data.symptoms,
        reason: data.reason || "",
        addition_notes: data.addition_notes || "",
        location: data.location,
        appointment_date: combinedDateTime.toISOString(),
        appointment_status: "PENDING",
      }

      // await createAppointmentMutation.mutateAsync(appointmentData)
      showSuccessToast("Appointment scheduled successfully")
      // onOpenChange(false)
      form.reset()
      if (onSave) onSave(appointmentData)
    } catch (error) {
      console.error("Failed to create appointment:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Schedule New Appointment</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="doctor_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Search Doctor</FormLabel>
                  <div className="relative">
                    <Input
                      ref={searchInputRef}
                      placeholder="Type to search doctors..."
                      value={searchDoctor}
                      onChange={(e) => {
                        setSearchDoctor(e.target.value)
                        if (!openDoctor) setOpenDoctor(true)
                      }}
                      className="w-full"
                      onClick={() => setOpenDoctor(true)}
                    />
                    {isLoadingDoctors && (
                      <div className="absolute right-3 top-3">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    )}
                    {openDoctor && (
                      <Card className="absolute w-full mt-1 shadow-lg border border-gray-200 z-50">
                        <ScrollArea className="h-[200px]">
                          <div className="p-2">
                            {!searchDoctor && (
                              <div className="text-sm text-gray-500 p-2 text-center">
                                Start typing to search doctors
                              </div>
                            )}
                            {isLoadingDoctors && (
                              <div className="text-sm text-gray-500 p-2 text-center">
                                Searching...
                              </div>
                            )}
                            {!isLoadingDoctors &&
                              doctorsData?.doctors?.length === 0 &&
                              searchDoctor && (
                                <div className="text-sm text-gray-500 p-2 text-center">
                                  No doctors found
                                </div>
                              )}
                            {doctorsData?.doctors?.map((doctor) => (
                              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                              <div
                                key={doctor.id}
                                className="p-3 hover:bg-gray-100 rounded-md cursor-pointer transition-colors"
                                onClick={() => handleDoctorSelect(doctor)}
                                onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                              >
                                <div className="font-medium text-base">
                                  {doctor.name}
                                </div>
                                <div className="text-sm text-gray-500 mt-1">
                                  {doctor.designation}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {doctor.qualifications}
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </Card>
                    )}
                  </div>
                  {selectedDoctor && (
                    <div className="mt-2 p-3 bg-blue-50 rounded-md border border-blue-100">
                      <div className="font-medium text-blue-700">
                        {selectedDoctor.name}
                      </div>
                      <div className="text-sm text-blue-600">
                        {selectedDoctor.designation}
                      </div>
                      <div className="text-xs text-blue-500">
                        {selectedDoctor.qualifications}
                      </div>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="appointment_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) =>
                            date < new Date() || date < new Date("1900-01-01")
                          }
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="appointment_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Time</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {timeSlots.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="symptoms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Symptoms</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason (Optional)</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="addition_notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!selectedDoctor}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            selectedDoctor
                              ? field.value
                              : "Select a doctor first"
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableLocations.map((location) => (
                        <SelectItem key={location} value={location}>
                          {location}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  "Schedule Appointment"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

AddAppointmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  patientId: PropTypes.number.isRequired,
  onSave: PropTypes.func,
}

export default AddAppointmentDialog
